/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)
Coded by www.creative-tim.com
 =========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext, useState } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function Tsp() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  const [percentDollarsQuestion, setPercentDollarsQuestion] = useState(true)
  const [percentDollarsQuestionRoth, setPercentDollarsQuestionRoth] =
    useState(true)

  const dollarsToPercent = (contributionAmount) => {
    return intakeState.baseSalary
      ? ((contributionAmount * 26) / intakeState.baseSalary) * 100
      : 0
  }
  const percentToDollars = (contributionPercent) => {
    return intakeState.baseSalary
      ? (intakeState.baseSalary / 26) * (contributionPercent / 100)
      : 0
  }

  const showTSPLoans = intakeState?.tspLoans
  const showRothTSP = intakeState?.tspRoth
  const showTSPContribution = intakeState?.tsp

  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your deduction details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          TSP
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} mt={2}>
            <Typography variant="h5">Traditional TSP Details</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'tsp',
              label: 'Do you contribute to the Traditional TSP?',
              placeholder: '',
              inputType: 'select',
              value: intakeState.tsp,
              onChange: updateIntakeStateAndSubmit((tsp) =>
                updateIntakeStateAtKey({ tsp })
              ),
              options: selectData.yesNo,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'tspBalance',
              label: 'Current TSP balance',
              placeholder: '$250,000.00',
              inputType: 'currencyFloat',
              value: intakeState.tspBalance,
              onChange: updateIntakeStateAndSubmit((tspBalance) =>
                updateIntakeStateAtKey({ tspBalance })
              ),
            })}
          </Grid>
          {showTSPContribution && (
            <>
              {percentDollarsQuestion ? (
                <Grid item xs={12} sm={6}>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspContribution',
                      label: 'What percentage do you contribute?',
                      placeholder: 'Ex. 5.0%',
                      inputType: 'percentage',
                      value: intakeState.tspContribution,
                      onChange: updateIntakeStateAndSubmit((tspContribution) =>
                        updateIntakeStateAtKey({
                          tspContribution,
                          tspContributionAmount:
                            percentToDollars(tspContribution),
                        })
                      ),
                    })}
                  </Grid>
                  <Box display="flex" justifyContent="flex-start">
                    <Button
                      variant="text"
                      color="info"
                      sx={{ height: '100%' }}
                      onClick={() => setPercentDollarsQuestion(false)}
                    >
                      Use amount contributed per pay period instead
                    </Button>
                  </Box>
                </Grid>
              ) : (
                <Grid item xs={12} sm={6}>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspContributionAmount',
                      label: 'What amount do you contribute per pay period?',
                      placeholder: '$135.00',
                      inputType: 'currencyFloat',
                      value: intakeState.tspContributionAmount,
                      onChange: updateIntakeStateAndSubmit(
                        (tspContributionAmount) =>
                          updateIntakeStateAtKey({
                            tspContributionAmount,
                            tspContribution: dollarsToPercent(
                              tspContributionAmount
                            ),
                          })
                      ),
                    })}
                  </Grid>
                  <Box display="flex" justifyContent="flex-start">
                    <Button
                      variant="text"
                      color="info"
                      sx={{ height: '100%' }}
                      onClick={() => setPercentDollarsQuestion(true)}
                    >
                      Use percentage contributed per pay period instead
                    </Button>
                  </Box>
                </Grid>
              )}
            </>
          )}

          {/* ROTH TSP */}
          <Grid item xs={12} mt={2}>
            <Typography variant="h5">Roth TSP Details</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'tspRoth',
              label: 'Do you contribute to the Roth TSP?',
              placeholder: '',
              inputType: 'select',
              value: intakeState.tspRoth,
              onChange: updateIntakeStateAndSubmit((tspRoth) =>
                updateIntakeStateAtKey({ tspRoth })
              ),
              options: selectData.yesNo,
            })}
          </Grid>

          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'tspRothBalance',
              label: 'Current Roth TSP balance',
              placeholder: '$250,000.00',
              inputType: 'currencyFloat',
              value: intakeState.tspRothBalance,
              onChange: updateIntakeStateAndSubmit((tspRothBalance) =>
                updateIntakeStateAtKey({ tspRothBalance })
              ),
            })}
          </Grid>

          {showRothTSP && (
            <>
              {percentDollarsQuestionRoth ? (
                <Grid item xs={12} sm={6}>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspRothContribution',
                      label: 'What percentage do you contribute?',
                      placeholder: 'Ex. 5.0%',
                      inputType: 'percentage',
                      value: intakeState.tspRothContribution,
                      onChange: updateIntakeStateAndSubmit(
                        (tspRothContribution) =>
                          updateIntakeStateAtKey({
                            tspRothContribution,
                            tspRothContributionAmount:
                              percentToDollars(tspRothContribution),
                          })
                      ),
                    })}
                  </Grid>
                  <Box display="flex" justifyContent="flex-start">
                    <Button
                      variant="text"
                      color="info"
                      sx={{ height: '100%' }}
                      onClick={() => setPercentDollarsQuestionRoth(false)}
                    >
                      Use amount contributed per pay period instead
                    </Button>
                  </Box>
                </Grid>
              ) : (
                <Grid item xs={12} sm={6}>
                  <Grid item xs={12}>
                    {renderMuiFormField({
                      autoFocus: false,
                      id: 'tspRothContributionAmount',
                      label: 'What amount do you contribute per pay period?',
                      placeholder: '$135.00',
                      inputType: 'currencyFloat',
                      value: intakeState.tspRothContributionAmount,
                      onChange: updateIntakeStateAndSubmit(
                        (tspRothContributionAmount) =>
                          updateIntakeStateAtKey({
                            tspRothContributionAmount,
                            tspRothContribution: dollarsToPercent(
                              tspRothContributionAmount
                            ),
                          })
                      ),
                    })}
                  </Grid>
                  <Box display="flex" justifyContent="flex-start">
                    <Button
                      variant="text"
                      color="info"
                      sx={{ height: '100%' }}
                      onClick={() => setPercentDollarsQuestionRoth(true)}
                    >
                      Use percentage contributed per pay period instead
                    </Button>
                  </Box>
                </Grid>
              )}
            </>
          )}
          <Grid item xs={12} mt={2}>
            <Typography variant="h5">TSP Loan Details</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'tspLoans',
              label: 'Do you have any TSP loans?',
              placeholder: '',
              inputType: 'select',
              value: intakeState.tspLoans,
              onChange: updateIntakeStateAndSubmit((tspLoans) =>
                updateIntakeStateAtKey({ tspLoans })
              ),
              options: selectData.yesNo,
            })}
          </Grid>

          {showTSPLoans && (
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                inputType: 'tspLoanReview',
              })}
            </Grid>
          )}
        </Grid>
      </Box>
    </Card>
  )
}

export default Tsp
